import Fuse from 'fuse.js';
import debounce from 'debounce';

const MIN_SEARCH_LENGTH = 3;

export default class FilterList {
    constructor(el) {
        this.DOM = { el: el };
        this.DOM.search = this.DOM.el.querySelector('[data-filter-search]');
        this.DOM.items = Array.from(this.DOM.el.querySelectorAll('[data-filter-item]'));
        this.DOM.pagination = this.DOM.el.querySelector('[data-filter-pagination]');
        this.DOM.empty = this.DOM.el.querySelector('[data-filter-empty]');
        this.DOM.count = this.DOM.el.querySelector('[data-filter-count]');
        this.DOM.mainHeader = document.querySelector('#main-header');

        this.perPage = parseInt(this.DOM.el.dataset.perPage, 10) || 6;
        this.page = 1;
        this.query = '';
        this.matchedIndices = null;

        let searchIndex = [];
        try {
            searchIndex = JSON.parse(this.DOM.el.dataset.filterSearchIndex || '[]');
        } catch (error) {
            searchIndex = [];
        }

        this.fuse = new Fuse(
            searchIndex.map((text, index) => ({ index, text })),
            {
                keys: ['text'],
                threshold: 0.3,
                ignoreLocation: true,
                minMatchCharLength: MIN_SEARCH_LENGTH,
            }
        );

        this.initEvents();
        this.render();
    }

    initEvents() {
        if (this.DOM.search) {
            this.DOM.search.addEventListener('input', debounce((e) => {
                this.query = e.target.value.trim();
                this.page = 1;
                this.matchedIndices = null;
                this.render();
            }, 200));
        }

        if (this.DOM.pagination) {
            this.DOM.pagination.addEventListener('click', this.onPaginationClick);
        }
    }

    onPaginationClick = (e) => {
        const trigger = e.target.closest('[data-page]');
        if (!trigger) {
            return;
        }

        e.preventDefault();

        const value = trigger.dataset.page;
        let page = this.page;

        if ('prev' === value) {
            page = this.page - 1;
        } else if ('next' === value) {
            page = this.page + 1;
        } else {
            page = parseInt(value, 10);
        }

        if (page !== this.page) {
            this.page = page;
            this.render();
            this.restoreFocus(value);
            this.scrollToTop();
        }
    }

    getMatchedIndices() {
        if (null !== this.matchedIndices) {
            return this.matchedIndices;
        }

        this.matchedIndices = this.findMatchedIndices();

        return this.matchedIndices;
    }

    findMatchedIndices() {
        const allIndices = this.DOM.items.map((item, index) => index);

        if (this.query.length < MIN_SEARCH_LENGTH) {
            return allIndices;
        }

        const tokens = this.query.split(/\s+/).filter((token) => token.length >= MIN_SEARCH_LENGTH);

        if (0 === tokens.length) {
            return allIndices;
        }

        let matched = null;

        for (const token of tokens) {
            const tokenMatches = new Set(this.fuse.search(token).map((result) => result.item.index));

            matched = null === matched
                ? tokenMatches
                : new Set([...matched].filter((index) => tokenMatches.has(index)));
        }

        return allIndices.filter((index) => matched.has(index));
    }

    render() {
        const matched = this.getMatchedIndices();
        const pageCount = Math.max(1, Math.ceil(matched.length / this.perPage));

        this.page = Math.min(Math.max(this.page, 1), pageCount);

        const start = (this.page - 1) * this.perPage;
        const visible = new Set(matched.slice(start, start + this.perPage));

        this.DOM.items.forEach((item, index) => {
            item.classList.toggle('d-none', !visible.has(index));
        });

        if (this.DOM.empty) {
            this.DOM.empty.classList.toggle('d-none', 0 !== matched.length);
        }

        if (this.DOM.count) {
            const noun = 1 === matched.length ? 'resultaat' : 'resultaten';
            this.DOM.count.textContent = `${matched.length} ${noun} gevonden`;
        }

        this.renderPagination(pageCount);
    }

    renderPagination(pageCount) {
        if (!this.DOM.pagination) {
            return;
        }

        if (pageCount <= 1) {
            this.DOM.pagination.innerHTML = '';

            return;
        }

        const items = [];

        items.push(this.navItem('prev', 'Vorige', 'text-right', this.page <= 1));

        for (const page of this.buildPageList(this.page, pageCount)) {
            if ('...' === page) {
                items.push('<li class="page-item"><span class="page-link" aria-hidden="true">…</span></li>');
                continue;
            }

            items.push(this.pageItem(page, page === this.page));
        }

        items.push(this.navItem('next', 'Volgende', 'text-left', this.page >= pageCount));

        this.DOM.pagination.innerHTML = `<ul class="pagination">${items.join('')}</ul>`;
    }

    buildPageList(current, total) {
        const delta = 1;
        const range = [];
        const result = [];
        let last;

        for (let page = 1; page <= total; page += 1) {
            if (1 === page || page === total || (page >= current - delta && page <= current + delta)) {
                range.push(page);
            }
        }

        for (const page of range) {
            if (last) {
                if (2 === page - last) {
                    result.push(last + 1);
                } else if (1 !== page - last) {
                    result.push('...');
                }
            }

            result.push(page);
            last = page;
        }

        return result;
    }

    pageItem(page, active) {
        const itemClass = `page-item${active ? ' active' : ''}`;
        const current = active ? ' aria-current="page"' : '';

        return `<li class="${itemClass}"><a class="page-link" href="#" data-page="${page}"${current}>${page}</a></li>`;
    }

    navItem(value, label, linkClass, disabled) {
        const itemClass = `page-item page-item--${value}${disabled ? ' disabled' : ''}`;
        const state = disabled ? ' aria-disabled="true" tabindex="-1"' : '';

        return `<li class="${itemClass}"><a class="page-link ${linkClass}" href="#" data-page="${value}"${state}>${label}</a></li>`;
    }

    restoreFocus(value) {
        if (!this.DOM.pagination) {
            return;
        }

        const trigger = this.DOM.pagination.querySelector(`[data-page="${value}"]`);
        const isUsable = trigger && !trigger.closest('.page-item').classList.contains('disabled');
        const target = isUsable
            ? trigger
            : this.DOM.pagination.querySelector('.page-item.active .page-link');

        if (target) {
            target.focus({ preventScroll: true });
        }
    }

    scrollToTop() {
        const offset = this.DOM.mainHeader ? this.DOM.mainHeader.clientHeight : 0;
        const top = this.DOM.el.getBoundingClientRect().top + window.pageYOffset - offset;

        window.scroll({ top: top, left: 0, behavior: 'smooth' });
    }
}

const filterListNodes = Array.from(document.querySelectorAll('[data-filter-list]'));
if (filterListNodes.length > 0) {
    filterListNodes.forEach((node) => {
        new FilterList(node);
    });
}
